import { useEffect, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { applyNotificationStatsToCache } from '../lib/notificationBadge';
import {
  useServiceWorker,
  type UseServiceWorkerOptions,
} from './useServiceWorker';
import { useNavigate, useOptionalPathname } from '../contexts/router';
import { toRouterPath } from './navigationUrl';
import { flushDeferredPageReload } from './pageReload';
import { normalizePushInvalidateMessage } from './pushInvalidate';
import { useNotificationBadgeSync } from './useNotificationBadgeSync';
import { startStaleAppReload } from './staleAppReload';

export interface PwaProviderProps
  extends Omit<UseServiceWorkerOptions, 'onNavigate' | 'onInvalidateQueries'> {
  children?: ReactNode;
  /** Override how navigations triggered by SW NAVIGATE_TO are handled. */
  onNavigate?: (url: string) => void;
}

/**
 * High-level wrapper that registers the service worker and connects:
 *   - SW NAVIGATE_TO → router.navigate
 *   - SW INVALIDATE_QUERIES → react-query.invalidateQueries
 *   - stale production builds → full page reload, except while a jam is open
 *
 * Mount this once near the top of your app (inside QueryClientProvider and
 * RouterProvider).
 */
export function PwaProvider({
  children,
  onNavigate,
  enabled = true,
  autoReload = true,
  ...options
}: PwaProviderProps) {
  let navigate: ((url: string) => void) | undefined;
  try {
    navigate = useNavigate();
  } catch {
    navigate = undefined;
  }
  const queryClient = useQueryClient();
  const pathname = useOptionalPathname();
  useNotificationBadgeSync();

  useEffect(() => {
    if (!autoReload) return;
    flushDeferredPageReload(() => window.location.reload());
  }, [autoReload, pathname]);

  const handleNavigate =
    onNavigate ??
    ((url: string) => {
      // React Router resolves absolute URLs as pathnames (`/https:/…`).
      const path = toRouterPath(url);
      if (navigate) navigate(path);
      else if (typeof window !== 'undefined') window.location.assign(url);
    });

  useEffect(() => {
    if (!enabled || !autoReload) return;
    return startStaleAppReload();
  }, [enabled, autoReload]);

  useServiceWorker({
    ...options,
    enabled,
    autoReload,
    onNavigate: handleNavigate,
    onInvalidateQueries: (message) => {
      const { keys, notificationStats } =
        normalizePushInvalidateMessage(message);
      if (notificationStats) {
        applyNotificationStatsToCache(queryClient, notificationStats);
      }
      if (keys.length === 0) {
        queryClient.invalidateQueries();
        return;
      }
      for (const key of keys) {
        queryClient.invalidateQueries({
          queryKey: key as readonly unknown[],
        });
      }
    },
  });

  return <>{children}</>;
}
